import { useEffect, useRef } from "react";
import { useTour } from "@reactour/tour";
import { useMediaQuery } from "@mui/material";

export function useDraggableTour() {
  const offset = useRef({ x: 0, y: 0 });
  const origin = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const cleanupRef = useRef(null);

  const { isOpen } = useTour();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  useEffect(() => {
    if (isOpen && isSmallScreen) {
      function attachDrag(popover) {
        // Reset position
        offset.current = { x: 0, y: 0 };
        popover.style.transform = "translate(0px, 0px)";
        popover.style.cursor = "grab";

        const onMouseDown = (e) => {
          if (e.target.closest("button, input, a, select, textarea")) return;
          isDragging.current = true;
          origin.current = {
            x: e.clientX - offset.current.x,
            y: e.clientY - offset.current.y,
          };
          popover.style.cursor = "grabbing";
          popover.style.userSelect = "none";
          e.preventDefault();
        };

        const onMouseMove = (e) => {
          if (!isDragging.current) return;
          offset.current = {
            x: e.clientX - origin.current.x,
            y: e.clientY - origin.current.y,
          };
          popover.style.transform = `translate(${offset.current.x}px, ${offset.current.y}px)`;
        };

        const onMouseUp = () => {
          if (!isDragging.current) return;
          isDragging.current = false;
          popover.style.cursor = "grab";
        };

        const onTouchStart = (e) => {
          const touch = e.touches[0];
          isDragging.current = true;
          origin.current = {
            x: touch.clientX - offset.current.x,
            y: touch.clientY - offset.current.y,
          };
        };

        const onTouchMove = (e) => {
          if (!isDragging.current) return;
          const touch = e.touches[0];
          offset.current = {
            x: touch.clientX - origin.current.x,
            y: touch.clientY - origin.current.y,
          };
          popover.style.transform = `translate(${offset.current.x}px, ${offset.current.y}px)`;
        };

        const onTouchEnd = () => {
          isDragging.current = false;
        };

        popover.addEventListener("mousedown", onMouseDown);
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);

        popover.addEventListener("touchstart", onTouchStart);
        popover.addEventListener("touchmove", onTouchMove);
        popover.addEventListener("touchend", onTouchEnd);

        // Return cleanup for this specific attachment
        return () => {
          popover.removeEventListener("mousedown", onMouseDown);
          window.removeEventListener("mousemove", onMouseMove);
          window.removeEventListener("mouseup", onMouseUp);

          popover.removeEventListener("touchstart", onTouchStart);
          popover.removeEventListener("touchmove", onTouchMove);
          popover.removeEventListener("touchend", onTouchEnd);
        };
      }

      let currentPopover = null;

      const observer = new MutationObserver(() => {
        const popover = document.querySelector(".reactour__popover");

        // Popover appeared or is a new node
        if (popover && popover !== currentPopover) {
          // Clean up listeners from previous node
          if (cleanupRef.current) {
            cleanupRef.current();
          }
          currentPopover = popover;
          cleanupRef.current = attachDrag(popover);
        }

        // Popover removed
        if (!popover && cleanupRef.current) {
          cleanupRef.current();
          cleanupRef.current = null;
          currentPopover = null;
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Attach immediately if popover already exists on mount
      const existing = document.querySelector(".reactour__popover");
      if (existing) {
        currentPopover = existing;
        cleanupRef.current = attachDrag(existing);
      }

      return () => {
        observer.disconnect();
        if (cleanupRef.current) {
          cleanupRef.current();
        }
      };
    } else {
      return;
    }
  }, [isSmallScreen, isOpen]);
}
